import { useHandler } from "@chatbotgang/etude/react/useHandler";
import TextField from "@mui/material/TextField";

import { INPUT_NUMBER_PREVENT_KEY } from "@/appConstant";
import { useControlledValue } from "@/hooks/useControlledValue";

type TextFieldProps = React.ComponentProps<typeof TextField>;

interface NumberInputProps extends Pick<
  TextFieldProps,
  "disabled" | "size" | "fullWidth" | "name" | "ref" | "sx" | "slotProps"
> {
  defaultValue?: number;
  value?: number;
  onChange: (value: number) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  min?: number;
  max?: number;
}

const clamp = (value: number, min: number, max?: number): number => {
  const floored = Math.max(min, value);
  return max !== undefined ? Math.min(floored, max) : floored;
};

export const NumberInput: React.FC<NumberInputProps> = function NumberInput({
  defaultValue = 0,
  value,
  onChange,
  onBlur,
  onFocus,
  min = 0,
  max,
  ...textFieldProps
}) {
  const [raw, setRaw] = useControlledValue(
    String(defaultValue),
    value === undefined ? undefined : String(value),
  );

  const handleChange = useHandler<
    React.ComponentProps<typeof TextField>["onChange"]
  >((e) => {
    const inputValue = e.target.value;
    setRaw(inputValue);

    if (inputValue !== "") {
      const parsed = Number(inputValue);
      if (!isNaN(parsed)) {
        onChange(clamp(parsed, min, max));
      }
    }
  });

  const handleBlur = useHandler(() => {
    const parsed = Number(raw);
    const resolved =
      raw === "" || isNaN(parsed) ? min : clamp(parsed, min, max);
    setRaw(String(resolved));
    onChange(resolved);
    onBlur?.();
  });

  const handleKeyDown = useHandler<
    React.ComponentProps<typeof TextField>["onKeyDown"]
  >((e) => {
    if (INPUT_NUMBER_PREVENT_KEY.includes(e.key)) {
      e.preventDefault();
    }
  });

  return (
    <TextField
      type="number"
      value={raw}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={onFocus}
      onKeyDown={handleKeyDown}
      {...textFieldProps}
    />
  );
};
